import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

const COLLECTION = 'financedDeals';

/**
 * Create a financed deal when investor accepts an offer and uploads signed contract.
 * Generates dealReference (REF-{fundCode}-{last6DealId}) for Interac payment matching.
 * @param {{ offerId: string, propertyId: string, ownerUid: string, lenderUid: string, lenderDisplayName?: string, signedContractFileRef: object, principalAmount?: number, interestRate?: number, termMonths?: number, startDate?: string, maturityDate?: string, interestPaymentDue?: string, interestCalculationMode?: string, lenderFundCode?: string }} data
 * @returns {Promise<string>} deal id
 */
export async function createFinancedDeal(data) {
  const {
    offerId,
    propertyId,
    ownerUid,
    lenderUid,
    lenderDisplayName,
    signedContractFileRef,
    principalAmount,
    interestRate,
    termMonths,
    startDate,
    maturityDate,
    interestPaymentDue,
    interestCalculationMode,
    lenderFundCode,
  } = data;
  if (!offerId || !propertyId || !ownerUid || !lenderUid || !signedContractFileRef) {
    throw new Error('offerId, propertyId, ownerUid, lenderUid and signedContractFileRef required');
  }
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    offerId,
    propertyId,
    ownerUid,
    lenderUid,
    lenderDisplayName: lenderDisplayName || null,
    signedContractFileRef,
    principalAmount: principalAmount ?? null,
    interestRate: interestRate ?? null,
    termMonths: termMonths ?? null,
    startDate: startDate || null,
    maturityDate: maturityDate || null,
    interestPaymentDue: interestPaymentDue === 'beginning_of_month' ? 'beginning_of_month' : 'end_of_month',
    interestCalculationMode: interestCalculationMode === 'monthly' ? 'monthly' : 'daily',
    status: 'active',
    fundsDisbursedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const fundCode = (lenderFundCode && String(lenderFundCode).trim())
    ? String(lenderFundCode).trim().toUpperCase().replace(/\s/g, '').slice(0, 20)
    : 'FND';
  const dealReference = 'REF-' + fundCode + '-' + docRef.id.slice(-6).toUpperCase();
  await updateDoc(docRef, { dealReference, updatedAt: serverTimestamp() });
  return docRef.id;
}

/**
 * @param {string} dealId
 * @returns {Promise<object | null>}
 */
export async function getFinancedDeal(dealId) {
  if (!dealId) return null;
  const docRef = doc(firestore, COLLECTION, dealId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * One-time fetch of financed deals for an owner (e.g. after creating a deal).
 * @param {string} ownerUid
 * @returns {Promise<Array<object>>}
 */
export async function getFinancedDealsByOwnerOnce(ownerUid) {
  if (!ownerUid) return [];
  const q = query(
    collection(firestore, COLLECTION),
    where('ownerUid', '==', ownerUid)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * One-time fetch of financed deals for a lender.
 * @param {string} lenderUid
 * @returns {Promise<Array<object>>}
 */
export async function getFinancedDealsByLenderOnce(lenderUid) {
  if (!lenderUid) return [];
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to financed deals where user is the owner (investor - "Projets financés").
 * @param {string} ownerUid
 * @param {(list: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeFinancedDealsByOwner(ownerUid, callback) {
  if (!ownerUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('ownerUid', '==', ownerUid)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribeFinancedDealsByOwner error', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to financed deals where user is the lender ("Financed Properties").
 * @param {string} lenderUid
 * @param {(list: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeFinancedDealsByLender(lenderUid, callback) {
  if (!lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribeFinancedDealsByLender error', err);
      callback([]);
    }
  );
}

/**
 * Get deal by offer id (to avoid duplicate deal for same offer).
 * ownerUid is required so the query is allowed by Firestore rules (query must filter by rule fields).
 * @param {string} offerId
 * @param {string} ownerUid - current user (investor) uid when checking before creating a deal
 * @returns {Promise<object | null>}
 */
export async function getFinancedDealByOfferId(offerId, ownerUid) {
  if (!offerId || !ownerUid) return null;
  const q = query(
    collection(firestore, COLLECTION),
    where('offerId', '==', offerId),
    where('ownerUid', '==', ownerUid)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Update deal (e.g. status, schedule, payments).
 * @param {string} dealId
 * @param {object} data
 */
export async function updateFinancedDeal(dealId, data) {
  if (!dealId) return;
  const docRef = doc(firestore, COLLECTION, dealId);
  const update = { ...data, updatedAt: serverTimestamp() };
  await updateDoc(docRef, update);
}
